import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import type { TriageFlowSelection } from "./types";
import {
  fetchTriageProcessFlows,
  type TriageProcessFlowOption,
} from "./triageProcessFlows";

const formFieldLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#262626",
  mb: 0.75,
};

export function createEmptyFlowSelection(): TriageFlowSelection {
  return { flowId: "", flowName: "", definitionId: "" };
}

export function isFlowSelectionComplete(value: TriageFlowSelection): boolean {
  return Boolean(value.flowId.trim() && value.flowName.trim());
}

type TriageRuleThenStepProps = Readonly<{
  value: TriageFlowSelection;
  onChange: (next: TriageFlowSelection) => void;
  showValidation?: boolean;
}>;

export default function TriageRuleThenStep({
  value,
  onChange,
  showValidation = false,
}: TriageRuleThenStepProps) {
  const [options, setOptions] = useState<TriageProcessFlowOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchTriageProcessFlows()
      .then((flows) => {
        if (cancelled) return;
        setOptions(flows);
      })
      .catch(() => {
        if (cancelled) return;
        setOptions([]);
        setLoadError("Failed to load process flows.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const showRequiredError =
    showValidation && !isFlowSelectionComplete(value);

  const selectValue = useMemo(() => {
    if (!value.flowId) return "";
    return value.flowId;
  }, [value.flowId]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      <Box sx={{ width: "100%" }}>
        <Typography sx={formFieldLabelSx}>
          Flow{" "}
          <Box component="span" sx={{ color: "#CF1322" }}>
            *
          </Box>
        </Typography>
        <FormControl
          fullWidth
          size="small"
          error={showRequiredError}
          sx={{
            width: "100%",
            minWidth: 0,
            flex: "1 1 auto",
            "& .MuiInputBase-root": {
              width: "100%",
            },
          }}
        >
          <Select
            displayEmpty
            value={selectValue}
            disabled={loading}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Box component="span" sx={{ color: "#9ca3af" }}>
                    Select Flow
                  </Box>
                );
              }
              const matched = options.find((item) => item.id === selected);
              return matched?.name ?? value.flowName ?? selected;
            }}
            onChange={(event) => {
              const nextId = String(event.target.value ?? "");
              const matched = options.find((item) => item.id === nextId);
              onChange({
                flowId: nextId,
                flowName: matched?.name ?? "",
                definitionId: matched?.definitionId ?? "",
              });
            }}
            MenuProps={{
              PaperProps: { sx: { maxHeight: 320 } },
            }}
          >
            {options.length === 0 ? (
              <MenuItem value="" disabled>
                {loading ? "Loading…" : "No flows available"}
              </MenuItem>
            ) : (
              options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))
            )}
          </Select>
          {showRequiredError ? (
            <FormHelperText>This field is required.</FormHelperText>
          ) : null}
          {loadError ? (
            <FormHelperText error>{loadError}</FormHelperText>
          ) : null}
        </FormControl>
      </Box>
    </Box>
  );
}
